import { useCallback, useEffect, useRef } from 'react';
const NUMBER_OF_CACHED_PANELS = 20;

interface CachedPanel {
  renderedAt: number;
  chartId: string;
}

type CachedPanels = CachedPanel[];

const useCachedPanels = () => {
  const cachedPanelsRef = useRef<CachedPanels>([]);

  const findCachedPanel = useCallback(chartId => {
    return cachedPanelsRef?.current?.find?.(panel => panel.chartId === chartId);
  }, []);

  const putCachedPanel = useCallback(
    chartId => {
      const cachedPanel = findCachedPanel(chartId);

      if (cachedPanel) {
        cachedPanel.renderedAt = new Date().getTime();
        return;
      }

      cachedPanelsRef.current.push({
        chartId,
        renderedAt: new Date().getTime()
      });
    },
    [findCachedPanel]
  );

  const updateCachedPanels = useCallback((chartsInViewLength: number) => {
    cachedPanelsRef?.current?.sort((pA, pB) => pB.renderedAt - pA.renderedAt);
    cachedPanelsRef.current = cachedPanelsRef?.current?.slice(
      0,
      Math.max(NUMBER_OF_CACHED_PANELS, chartsInViewLength)
    );
  }, []);

  const isPanelCached = useCallback(chartId => !!findCachedPanel(chartId), [
    findCachedPanel
  ]);

  const getCachedPanelIds = useCallback(
    () => cachedPanelsRef.current.map(panel => panel.chartId),
    []
  );

  const cleanCachedPanels = useCallback((existingPanelIds = []) => {
    if (!existingPanelIds.length) {
      cachedPanelsRef.current = [];
    } else {
      cachedPanelsRef.current = cachedPanelsRef.current?.filter(panel =>
        existingPanelIds.includes(panel.chartId)
      );
    }
  }, []);

  useEffect(() => {
    return () => {
      cachedPanelsRef.current = [];
    };
  }, []);

  return {
    isPanelCached,
    putCachedPanel,
    updateCachedPanels,
    getCachedPanelIds,
    cleanCachedPanels
  };
};

export default useCachedPanels;
